import React, { memo, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  ButtonIcon,
  FooterActionBar,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { CF_INF_MED, CF_NEU_BLD, CF_NEU_DIS, testIds } from '../../constants';

import './UiWrapper.css';

export const WRAPPER_MODE = {
  INLINE_COLLAPSED: 'inline-collapsed',
  INLINE: 'inline',
  EXPANDED: 'expanded',
  VIEWING: 'viewing_full_config',
};

export type WrapperMode = (typeof WRAPPER_MODE)[keyof typeof WRAPPER_MODE];

interface UiWrapperProps {
  id: string;
  mode: WrapperMode;
  title: string;
  isActive: boolean;
  isRightWindowOpen: boolean;
  isEdited?: boolean;
  isDisabled?: boolean;
  isCollapseDisabled?: boolean;
  isExpandDisabled?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  onClose?: () => void;
  className?: string;
  childClassName?: string;
  footerClassName?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * UiWrapper
 */
function UiWrapper({
  id,
  mode,
  title,
  isActive,
  isRightWindowOpen,
  isEdited,
  isDisabled,
  isCollapseDisabled,
  isExpandDisabled,
  onExpand,
  onClose,
  onCollapse,
  className,
  childClassName,
  footerClassName,
  children,
  footer,
}: UiWrapperProps) {
  const isInline = Boolean(mode === WRAPPER_MODE.INLINE);
  const isInlineCollapsed = Boolean(mode === WRAPPER_MODE.INLINE_COLLAPSED);
  const isExpanded = Boolean(mode === WRAPPER_MODE.EXPANDED);
  const isViewing = Boolean(mode === WRAPPER_MODE.VIEWING);

  const renderHeader = () => {
    if (isInline) {
      return (
        <>
          {!isExpandDisabled && (
            <ButtonIcon
              kind='ghost'
              ariaLabel='Full Page'
              icon={<Icon kind='Fullscreen' size='medium' />}
              onClick={onExpand}
              isDisabled={isDisabled}
              className='UiWrapper-headerExpander'
              dataTestId={testIds.GEN_UI_WRAPPER_EXPAND_BTN}
            />
          )}
          <ButtonIcon
            kind='ghost'
            ariaLabel='Close Savi'
            icon={<Icon kind='Close' size='medium' />}
            onClick={onClose}
            isDisabled={isDisabled}
            className={
              isExpandDisabled
                ? 'UiWrapper-headerClose UiWrapper-headerExpander'
                : 'UiWrapper-headerClose'
            }
            dataTestId={testIds.GEN_UI_WRAPPER_CLOSE_BTN}
          />
        </>
      );
    }

    if (isInlineCollapsed) {
      return (
        <ButtonIcon
          kind='outlinedNeutral'
          ariaLabel='Expand'
          icon={<Icon kind='Edit' size='medium' />}
          onClick={onExpand}
          isDisabled={isDisabled ?? !isActive}
          className='UiWrapper-headerExpander'
          dataTestId={testIds.GEN_UI_WRAPPER_EXPAND_BTN}
        />
      );
    }

    if (isExpanded) {
      return (
        <ButtonIcon
          kind='ghost'
          ariaLabel='Collapse'
          icon={<Icon kind='Unfix' size='medium' />}
          onClick={onCollapse}
          isDisabled={isDisabled}
          className='UiWrapper-headerCollapse'
          dataTestId={testIds.GEN_UI_WRAPPER_COLLAPSE_BTN}
        />
      );
    }

    if (isViewing) {
      return (
        <Box tag='div' className='UiWrapper-isViewing'>
          <Typography kind='body3' color={CF_INF_MED}>
            <FormattedMessage
              id='7AA4bt'
              defaultMessage='Viewing'
              description='Text to indicate that the user is in viewing mode in the UiWrapper component'
            />
          </Typography>
          <Icon kind='ArrowRight' size='smallMedium' color={CF_INF_MED} />
        </Box>
      );
    }

    return null;
  };

  return (
    <div
      data-scroll-id={id}
      className={cx(
        'UiWrapper',
        mode === WRAPPER_MODE.INLINE_COLLAPSED &&
          'UiWrapper--isInlineCollapsed',
        mode === WRAPPER_MODE.INLINE && 'UiWrapper--isInline',
        mode === WRAPPER_MODE.EXPANDED && 'UiWrapper--isExpanded',
        mode === WRAPPER_MODE.VIEWING && 'UiWrapper--isViewing',
        className
      )}
      data-testid={testIds.GEN_UI_WRAPPER_ID}>
      {!isExpanded && (
        <Box
          tag='div'
          className={cx(
            'UiWrapper-header',
            isRightWindowOpen && 'UiWrapper-header--isRightWindowOpen'
          )}>
          {!isEdited && (
            <Icon
              kind={isActive ? 'StatusToDo' : 'CheckCircleFilledSeparateColors'}
              color={
                isActive
                  ? '--color-foreground-warning-subtle'
                  : '--color-foreground-success-subtle'
              }
              size='large'
            />
          )}
          <Typography kind='h3' color={isEdited ? CF_NEU_DIS : CF_NEU_BLD}>
            {title}
          </Typography>
          {renderHeader()}
        </Box>
      )}
      {isExpanded && onCollapse && !isCollapseDisabled && (
        <ButtonIcon
          kind='ghost'
          ariaLabel='Collapse'
          icon={<Icon kind='Unfix' size='medium' />}
          onClick={onCollapse}
          isDisabled={Boolean(isDisabled)}
          className='UiWrapper-headerCollapse'
          dataTestId={testIds.GEN_UI_WRAPPER_COLLAPSE_BTN}
        />
      )}
      {(isInline || isExpanded) && (
        <>
          <Box
            tag='div'
            className={cx(
              'UiWrapper-body',
              !footer && 'UiWrapper-body--isNoFooter',
              childClassName
            )}>
            {children}
          </Box>
          {footer && (
            <FooterActionBar
              className={cx('UiWrapper-footer', footerClassName)}>
              {footer}
            </FooterActionBar>
          )}
        </>
      )}
    </div>
  );
}

UiWrapper.defaultProps = {
  isDisabled: false,
  isEdited: false,
  onExpand: undefined,
  onCollapse: undefined,
  isCollapseDisabled: false,
  isExpandDisabled: false,
  onClose: undefined,
  className: null,
  childClassName: null,
  footerClassName: null,
  footer: undefined,
};

export default memo(UiWrapper);
